'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
import React, { createContext, memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Endpoint, Resource, Service } from '../../lib';
import { EndpointArticle } from '../endpoint-article';
import { IntroductionArticle } from '../introduction-article';
import { ResourceArticle } from '../resource-article';
import { DocumentationNavBar } from './DocumentationNavBar';
import { DocumentationSidebar } from './DocumentationSidebar';
import { SidebarLayout } from './sidebar';
const List = React.forwardRef(function DocumentationElementList(_a, ref) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return _jsx("div", Object.assign({ className: clsx(className, 'divide-y divide-zinc-950/10 dark:divide-white/10') }, props, { ref: ref }));
});
const DocumentationContext = createContext({
    observer: null,
    elements: []
});
const ElementArticle = memo(function ElementArticle({ index }) {
    const { observer, elements } = useContext(DocumentationContext);
    useEffect(() => {
        const htmlElement = document.getElementById(`article-${element.slug}`);
        if (!htmlElement) {
            return;
        }
        observer === null || observer === void 0 ? void 0 : observer.observe(htmlElement);
        return () => observer === null || observer === void 0 ? void 0 : observer.unobserve(htmlElement);
    }, [observer]);
    const element = elements[index];
    return (_jsxs(_Fragment, { children: [element instanceof Service && _jsx(IntroductionArticle, { service: element, id: `article-${element.slug}` }), element instanceof Resource && _jsx(ResourceArticle, { resource: element, id: `article-${element.slug}` }), element instanceof Endpoint && _jsx(EndpointArticle, { endpoint: element, id: `article-${element.slug}` })] }));
});
export function ReferenceDocumentation({ openAPIs, onSlugChange }) {
    const services = useMemo(() => {
        return openAPIs.map((openAPI) => new Service(openAPI));
    }, [openAPIs]);
    const [selectedService, setSelectedService] = useState(services.length > 0 ? services[0] : null);
    const [currentElement, setCurrentElement] = useState(null);
    useEffect(() => {
        if (onSlugChange) {
            if (currentElement) {
                onSlugChange(currentElement.slug);
            }
            else {
                onSlugChange(null);
            }
        }
    }, [onSlugChange, currentElement]);
    const elements = useMemo(() => {
        return selectedService
            ? [
                selectedService,
                ...selectedService.resources.flatMap((resource) => [resource, ...resource.endpoints]),
                ...selectedService.otherEndpoints
            ]
            : [];
    }, [selectedService]);
    const container = useRef(null);
    const virtuoso = useRef(null);
    const observer = useRef(null);
    const [observerState, setObserverState] = useState(null);
    useEffect(() => {
        observer.current = new IntersectionObserver((entries) => {
            var _a, _b;
            const intersectingEntries = entries
                .filter((entry) => entry.isIntersecting)
                .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
            if (intersectingEntries.length > 0) {
                const id = intersectingEntries[0].target.getAttribute('id');
                const slug = (_a = id === null || id === void 0 ? void 0 : id.substring('article-'.length)) !== null && _a !== void 0 ? _a : '';
                setCurrentElement((_b = elements.find((element) => element.slug === slug)) !== null && _b !== void 0 ? _b : null);
            }
        }, {
            root: container.current
        });
        setObserverState(observer.current);
        return () => {
            var _a;
            (_a = observer.current) === null || _a === void 0 ? void 0 : _a.disconnect();
            observer.current = null;
            setObserverState(null);
        };
    }, [elements]);
    return (_jsxs(DocumentationContext.Provider, { value: { observer: observerState, elements }, children: [_jsx(DocumentationNavBar, { services: services, selectedService: selectedService, setSelectedService: (service) => {
                    setSelectedService(service);
                    setCurrentElement(null);
                }, selection: currentElement, onSelect: (element) => {
                    var _a;
                    setCurrentElement(element);
                    const index = element ? elements.indexOf(element) : 0;
                    if (index >= 0) {
                        (_a = virtuoso.current) === null || _a === void 0 ? void 0 : _a.scrollToIndex(index);
                    }
                }, className: "p-2 border-b border-zinc-950/10 dark:bg-white/10 lg:hidden" }), _jsx(SidebarLayout, { sidebar: _jsx(DocumentationSidebar, { services: services, selectedService: selectedService, setSelectedService: (service) => {
                        setSelectedService(service);
                        setCurrentElement(null);
                    }, selection: currentElement, onSelect: (element) => {
                        var _a;
                        setCurrentElement(element);
                        const index = element ? elements.indexOf(element) : 0;
                        if (index >= 0) {
                            (_a = virtuoso.current) === null || _a === void 0 ? void 0 : _a.scrollToIndex(index);
                        }
                    } }), children: _jsx("div", { ref: container, className: "h-full", children: _jsx(Virtuoso, { ref: virtuoso, totalCount: elements.length, itemContent: (index) => _jsx(ElementArticle, { index: index }), components: { List } }) }) })] }));
}
